import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, Fragment, memo } from 'react';

import ContractProfileEntities from '@/components/ContractProfileEntities';
import { DRUG_CATEGORIES } from '@/constants/programs';
import type { ContractActiveProgram } from '@/types/contract';
import { Card, Tooltip, Typography } from '@/ui';
import { familyColumnLabel, PHASE_TYPES } from '@/utils/contractLabels';

import './styles.css';
import type { Props } from './types';

// [phase type, column label, full name for the header tooltip]; Dual variants get their own column.
const phases = [
  ...Object.entries(PHASE_TYPES).map(([phaseType, { abbreviation, name }]) => [
    phaseType,
    abbreviation,
    name
  ]),
  ['dual', 'Dual', 'Dual diagnosis variants']
];

const ContractCoverageProfileCard: FC<Props> = ({
  label,
  entities,
  activePrograms,
  families,
  gapsOnly,
  newEntityIds,
  version
}) => {
  // Every entity in a profile contracts the same programs.
  const ids = new Set(entities[0].program_ids);
  const count = activePrograms.filter((program) => ids.has(program.id)).length;
  const hasGap = (programs: ContractActiveProgram[]) =>
    programs.some((program) => !ids.has(program.id));
  const offered = (familyId: string, phase: string) =>
    activePrograms.filter(
      (program) =>
        program.program_family_id === familyId &&
        (phase === 'dual' ? program.is_dual : !program.is_dual && program.phase_type === phase)
    );
  // Families without a drug category still have programs, so they get their own group.
  const coverageCategories = families.some((family) => !family.drug_category)
    ? [...DRUG_CATEGORIES, { value: '', label: 'Uncategorized' }]
    : DRUG_CATEGORIES;
  const inCategory = (family: { drug_category: string | null }, category: string) =>
    (family.drug_category ?? '') === category;

  return (
    <Card
      padding="none"
      role="region"
      aria-label={`Coverage profile ${label}`}
      className="min-w-0 overflow-hidden">
      <div className="flex items-center gap-3 border-b border-uniform-gray-200 p-4 dark:border-uniform-gray-700">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-uniform-gray-900 font-semibold text-white dark:bg-uniform-gray-100 dark:text-uniform-gray-900">
          {label}
        </span>
        <div>
          <Typography variant="subheading" as="h3">
            Coverage profile {label}{' '}
            <Typography as="span" muted weight="normal">
              · {entities.length} {entities.length === 1 ? 'entity' : 'entities'}
            </Typography>
          </Typography>
          <Typography muted>
            {count} of {activePrograms.length} active programs
          </Typography>
        </div>
      </div>
      <div className="grid 2xl:grid-cols-[minmax(480px,3fr)_minmax(0,2fr)]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] table-fixed">
            <thead>
              <tr className="border-b border-uniform-gray-200 dark:border-uniform-gray-700">
                <th scope="col" className="w-[40%] p-2 text-left">
                  <Typography variant="caption" muted>
                    Program family
                  </Typography>
                </th>
                {phases.map(([key, phaseLabel, fullName]) => (
                  <th key={key} scope="col" className="p-2 text-center">
                    <Tooltip content={fullName} focusable>
                      <Typography variant="caption" muted>
                        {phaseLabel}
                      </Typography>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coverageCategories.map((category) => {
                const categoryFamilies = families.filter(
                  (family) =>
                    inCategory(family, category.value) &&
                    (!gapsOnly ||
                      hasGap(
                        activePrograms.filter((program) => program.program_family_id === family.id)
                      ))
                );
                if (!categoryFamilies.length) return null;
                return (
                  <Fragment key={category.value || 'uncategorized'}>
                    <tr className="border-b border-uniform-gray-200 bg-uniform-gray-50 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
                      <th colSpan={phases.length + 1} className="p-2 text-left">
                        <Typography variant="label" as="span">
                          {category.label}
                        </Typography>
                      </th>
                    </tr>
                    {categoryFamilies.map((family) => (
                      <tr
                        key={family.id}
                        className="border-b border-uniform-gray-100 dark:border-uniform-gray-700">
                        <th scope="row" className="p-2 text-left">
                          <Typography as="span" weight="normal">
                            {familyColumnLabel(family)}
                          </Typography>
                        </th>
                        {phases.map(([phase]) => {
                          const programs = offered(family.id, phase);
                          const missing = programs.filter((program) => !ids.has(program.id));
                          const title = programs.length
                            ? programs
                                .map(
                                  (program) =>
                                    `${program.name}: ${ids.has(program.id) ? 'contracted' : 'not contracted'}`
                                )
                                .join(', ')
                            : 'Not offered';
                          return (
                            <td
                              key={phase}
                              aria-label={title}
                              className={`p-2 text-center ${programs.length === 0 ? 'bg-uniform-gray-200 dark:bg-uniform-gray-700' : ''}`}>
                              {programs.length > 0 && (missing.length > 0 || !gapsOnly) && (
                                <Tooltip content={title}>
                                  <FontAwesomeIcon
                                    icon={missing.length ? faXmark : faCheck}
                                    aria-hidden="true"
                                    className={
                                      missing.length ? 'text-orange-600' : 'text-uniform-green-700'
                                    }
                                  />
                                </Tooltip>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="relative border-t border-uniform-gray-200 2xl:border-l 2xl:border-t-0 dark:border-uniform-gray-700">
          <ContractProfileEntities
            entities={entities}
            newEntityIds={newEntityIds}
            version={version}
          />
        </div>
      </div>
    </Card>
  );
};

export default memo(ContractCoverageProfileCard);
